/**
 * teamMemberCounts — pure People/Teams tally source.
 *
 * bd/AI-5few (S2.1): the People/Teams cards used to count ONLY the legacy
 * `MANAGER`/`EXECUTIVE` levels, so every real area team (whose memberships use
 * the Apr-28-2026 `TEAM_HEAD`/`FIELD_EXECUTIVE` levels) rendered "0 Managers /
 * 0 Executives". This maps the NEW levels to the two tallies while still
 * honouring legacy rows, so mixed data counts correctly.
 *
 *   TEAM_HEAD (new) + MANAGER (legacy)       → managers
 *   FIELD_EXECUTIVE (new) + EXECUTIVE (legacy) → executives
 *
 * DEPARTMENT_HEAD and BACKEND_STAFF are intentionally in NEITHER tally (the
 * head is shown separately; desk staff are not field managers/executives).
 * `total` counts every active member regardless of level.
 *
 * PURE + framework-free so it is trivially unit-testable and reusable by both
 * the team-card summary and any future denormalised backend counts.
 */
import type { TeamMemberLevel } from '@/api/teams';

export interface CountableMembership {
  level: TeamMemberLevel | string;
  /** ISO string / Date when the member left the team; null/undefined = active. */
  leftAt?: string | Date | null;
}

export interface TeamLevelCounts {
  managers: number;
  executives: number;
  total: number;
}

const MANAGER_LEVELS = new Set<string>(['TEAM_HEAD', 'MANAGER']);
const EXECUTIVE_LEVELS = new Set<string>(['FIELD_EXECUTIVE', 'EXECUTIVE']);

/**
 * Count managers / executives / total over a team's memberships. Only ACTIVE
 * memberships (`leftAt` null/undefined) are counted.
 */
export function countTeamMembers(memberships: CountableMembership[] | null | undefined): TeamLevelCounts {
  const active = (memberships ?? []).filter((m) => !m.leftAt);
  return {
    managers: active.filter((m) => MANAGER_LEVELS.has(m.level)).length,
    executives: active.filter((m) => EXECUTIVE_LEVELS.has(m.level)).length,
    total: active.length,
  };
}
